/**
 * The things a film's people pick up, push, drive and trip over — and the furniture of
 * the frame itself: the washes of sky and grass, the captions, the wipe between scenes.
 *
 * Each is drawn about its own origin, in stage units, by a function of where it is and
 * how far through its own motion it is. None of them keeps state.
 */

import type { Ink, Paint, Pt } from "./ink.js";
import { noise } from "./ink.js";
import type { View } from "./film.js";
import type { Mix } from "./sound.js";
import { clamp, ease, mix, span } from "./timing.js";

// ---- the frame ---------------------------------------------------------------------

/** Where the ground is in a scene shot from a person's height: the line feet stand on. */
export const GROUND = 262;

/** A wash of sky down to the horizon and of grass below it, across the whole view. */
export function landscape(ink: Ink, view: View, horizon: number, opts: { sky?: boolean } = {}): void {
  const p = ink.palette;
  const left = view.left - 40;
  const right = view.right + 40;
  if (opts.sky !== false) {
    ink.wash(
      [
        { x: left, y: view.top - 40 },
        { x: right, y: view.top - 40 },
        { x: right, y: horizon },
        { x: left, y: horizon },
      ],
      p.sky,
      0.55,
      0,
    );
  }
  const edge: Pt[] = [];
  for (let x = left; x <= right + 30; x += 30) {
    edge.push({ x, y: horizon - 6 - Math.abs(noise(Math.round(x), 7)) * 10 });
  }
  ink.wash([...edge, { x: right, y: view.bottom + 40 }, { x: left, y: view.bottom + 40 }], p.grass, 0.85, 0);
  ink.line(left, horizon, right, horizon, { width: 2.6, wobble: 2 });
  // Tufts.
  for (let x = Math.ceil(left / 70) * 70; x < right; x += 70) {
    const y = horizon + 24 + Math.abs(noise(x, 3)) * (view.bottom - horizon - 40);
    if (y > view.bottom - 10) {
      continue;
    }
    ink.curve(
      [
        { x: x - 6, y },
        { x: x - 3, y: y - 9 },
      ],
      { width: 1.8, alpha: 0.5 },
    );
    ink.curve(
      [
        { x: x + 3, y },
        { x: x + 6, y: y - 11 },
      ],
      { width: 1.8, alpha: 0.5 },
    );
  }
}

export interface CaptionStyle {
  size?: number;
  y?: number;
  /** A word or figure in the line drawn in the accent: the price. */
  accent?: string;
}

/**
 * A line of the film's own words: it pops onto the paper, an accent stroke underlines it,
 * and it lifts off again at `end`.
 */
export function caption(ink: Ink, text: string, t: number, start: number, end: number, style: CaptionStyle = {}): void {
  if (t < start || t > end + 0.25) {
    return;
  }
  const p = ink.palette;
  const size = style.size ?? 62;
  const y = style.y ?? -372;
  const pop = ease.back(span(t, start, start + 0.32));
  const out = span(t, end, end + 0.25);
  const alpha = clamp(span(t, start, start + 0.12) - out);
  const { ctx } = ink;
  ctx.save();
  ctx.translate(0, y - out * 30);
  ctx.scale(mix(0.7, 1, pop), mix(0.7, 1, pop));
  ctx.rotate(-0.018);
  const textStyle = { size, weight: 800, alpha };
  if (style.accent !== undefined && text.includes(style.accent)) {
    const [before = "", after = ""] = text.split(style.accent);
    const total = ink.measure(text, textStyle);
    let x = -total / 2;
    for (const [part, color] of [
      [before, p.ink],
      [style.accent, p.accent],
      [after, p.ink],
    ] as const) {
      ink.text(part, x, 0, { ...textStyle, align: "left", color });
      x += ink.measure(part, textStyle);
    }
  } else {
    ink.text(text, 0, 0, textStyle);
  }
  const width = ink.measure(text, textStyle);
  const draw = ease.out(span(t, start + 0.15, start + 0.5));
  if (draw > 0) {
    ink.curve(
      [
        { x: -width / 2 - 6, y: size * 0.62 },
        { x: 0, y: size * 0.52 },
        { x: width / 2 + 10, y: size * 0.6 },
      ],
      { width: 7, color: p.accent, alpha, draw },
    );
  }
  ctx.restore();
}

/** A breath of air under each caption as it pops on, and the page pulled across at each wipe. */
export function wordsSound(mix: Mix, lines: readonly { start: number }[], wipes: readonly number[]): void {
  for (const line of lines) {
    mix.whoosh(line.start - 0.04, 0.3, { from: 900, to: 4200, gain: 0.22 });
  }
  for (const at of wipes) {
    mix.whoosh(at - 0.28, 0.56, { from: 500, to: 2400, gain: 0.42 });
  }
}

/**
 * Between two scenes: a fresh sheet of paper pulled across the frame, right to left. It
 * covers the frame by `at` and has uncovered the next scene a quarter second later.
 */
export function wipe(ink: Ink, view: View, t: number, at: number): void {
  const half = 0.28;
  if (t < at - half || t > at + half) {
    return;
  }
  const p = ink.palette;
  const width = view.right - view.left;
  let leftEdge: number;
  let rightEdge: number;
  if (t < at) {
    const k = ease.inOut(span(t, at - half, at));
    leftEdge = mix(view.right, view.left, k);
    rightEdge = view.right + 60;
  } else {
    const k = ease.inOut(span(t, at, at + half));
    leftEdge = view.left - 60;
    rightEdge = mix(view.right, view.left, k);
  }
  if (rightEdge <= leftEdge) {
    return;
  }
  const { ctx } = ink;
  ctx.save();
  ctx.fillStyle = p.paper;
  ctx.fillRect(leftEdge, view.top - 20, rightEdge - leftEdge, view.bottom - view.top + 40);
  // The sheet's own grid, so it reads as more of the same paper.
  ctx.strokeStyle = p.grid;
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = Math.ceil(leftEdge / 30) * 30; x < rightEdge; x += 30) {
    ctx.moveTo(x, view.top);
    ctx.lineTo(x, view.bottom);
  }
  for (let y = Math.ceil(view.top / 30) * 30; y < view.bottom; y += 30) {
    ctx.moveTo(leftEdge, y);
    ctx.lineTo(rightEdge, y);
  }
  ctx.stroke();
  ctx.restore();
  for (const edge of [leftEdge, rightEdge]) {
    if (edge > view.left - 30 && edge < view.right + 30) {
      ink.wash(
        [
          { x: edge - 14, y: view.top - 20 },
          { x: edge, y: view.top - 20 },
          { x: edge, y: view.bottom + 20 },
          { x: edge - 14, y: view.bottom + 20 },
        ],
        p.shadow,
        0.7,
        0,
      );
      ink.line(edge, view.top - 20, edge, view.bottom + 20, { width: 2.4, overshoot: false });
    }
  }
  void width;
}

/** A camera: what point of the world is in the middle of the stage, and how close. */
export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

/** Draws `scene` through a camera, with the pen's lines kept the same weight on screen. */
export function shoot(ink: Ink, camera: Camera, scene: (view: View) => void, view: View): void {
  const { ctx } = ink;
  ctx.save();
  ctx.scale(camera.zoom, camera.zoom);
  ctx.translate(-camera.x, -camera.y);
  const before = ink.weight;
  ink.weight = before / Math.max(1, camera.zoom);
  scene({
    left: camera.x + view.left / camera.zoom,
    right: camera.x + view.right / camera.zoom,
    top: camera.y + view.top / camera.zoom,
    bottom: camera.y + view.bottom / camera.zoom,
  });
  ink.weight = before;
  ctx.restore();
}

// ---- small effects ------------------------------------------------------------------

/** A little burst where a hammer lands. */
export function impactStar(ink: Ink, x: number, y: number, k: number, size = 1): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  for (let i = 0; i < 5; i += 1) {
    const a = -Math.PI / 2 + (i - 2) * 0.55;
    const r0 = 8 * size;
    const r1 = (14 + 12 * k) * size;
    ink.line(x + Math.cos(a) * r0, y + Math.sin(a) * r0, x + Math.cos(a) * r1, y + Math.sin(a) * r1, {
      width: 2.6,
      color: i % 2 === 0 ? p.accent : p.ink,
      alpha: k,
      overshoot: false,
    });
  }
}

/** A puff of dust: a few rings that grow and fade. */
export function dust(ink: Ink, x: number, y: number, k: number, size = 1): void {
  if (k <= 0 || k >= 1) {
    return;
  }
  const p = ink.palette;
  for (let i = 0; i < 4; i += 1) {
    const dx = (i - 1.5) * 16 * size * (0.6 + k);
    ink.circle(x + dx, y - 6 * size - k * 14 * size * (i % 2), (6 + 10 * k) * size, {
      fill: p.shadow,
      fillAlpha: (1 - k) * 0.8,
      alpha: 1 - k,
      width: 1.6,
      offset: 0,
    });
  }
}

/** A four-pointed glint, for the shine on a finished thing. */
export function sparkle(ink: Ink, x: number, y: number, k: number, size = 18): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  const s = size * Math.sin(k * Math.PI);
  const w = s * 0.22;
  ink.shape(
    [
      { x, y: y - s },
      { x: x + w, y: y - w },
      { x: x + s, y },
      { x: x + w, y: y + w },
      { x, y: y + s },
      { x: x - w, y: y + w },
      { x: x - s, y },
      { x: x - w, y: y - w },
    ],
    { fill: p.accent, width: 1.8, overshoot: false, offset: 0 },
  );
}

export function speedLines(ink: Ink, x: number, y: number, k: number, count = 4, length = 70): void {
  for (let i = 0; i < count; i += 1) {
    const yy = y + i * 18;
    const shift = ((k * 3 + i * 0.37) % 1) * 30;
    ink.line(x - shift - length, yy, x - shift, yy, { width: 2.2, alpha: 0.6, overshoot: false });
  }
}

// ---- held things --------------------------------------------------------------------

/** A hammer, gripped at the end of the handle: for `Figure.hold`. */
export function hammerInHand(ink: Ink, hand: Pt, angle: number): void {
  const p = ink.palette;
  const reachX = Math.cos(angle);
  const reachY = Math.sin(angle);
  const end = { x: hand.x + reachX * 26, y: hand.y + reachY * 26 };
  ink.line(hand.x - reachX * 4, hand.y - reachY * 4, end.x, end.y, { width: 4.6, color: p.woodDark, overshoot: false });
  const nx = -reachY;
  const ny = reachX;
  ink.shape(
    [
      { x: end.x - nx * 9 - reachX * 3, y: end.y - ny * 9 - reachY * 3 },
      { x: end.x + nx * 7 - reachX * 3, y: end.y + ny * 7 - reachY * 3 },
      { x: end.x + nx * 7 + reachX * 4, y: end.y + ny * 7 + reachY * 4 },
      { x: end.x - nx * 9 + reachX * 4, y: end.y - ny * 9 + reachY * 4 },
    ],
    { fill: p.roof, width: 2, overshoot: false, offset: 0 },
  );
}

/** A board carried on the shoulder: for a figure's hand. */
export function boardOnShoulder(length: number): (ink: Ink, hand: Pt) => void {
  return (ink, hand) => {
    ink.rect(hand.x - length * 0.5, hand.y - 12, length, 9, { fill: ink.palette.wood, width: 2, offset: 1 });
  };
}

// ---- the garage and what comes out of it -----------------------------------------------

export function garage(ink: Ink, open: number): void {
  const p = ink.palette;
  // The house front, a low gable over a wide door.
  const wall: Pt[] = [
    { x: -330, y: 262 },
    { x: 330, y: 262 },
    { x: 330, y: 2 },
    { x: 0, y: -92 },
    { x: -330, y: 2 },
  ];
  ink.shape(wall, { fill: p.paper, width: 3 });
  ink.wash(wall, p.wood, 0.35, 3);
  ink.shape(
    [
      { x: -372, y: 16 },
      { x: 0, y: -112 },
      { x: 372, y: 16 },
      { x: 372, y: 34 },
      { x: 0, y: -94 },
      { x: -372, y: 34 },
    ],
    { fill: p.roof, width: 3 },
  );
  ink.circle(0, -34, 16, { fill: p.glass, width: 2.4 });
  ink.line(-16, -34, 16, -34, { width: 1.6, overshoot: false });
  // The opening, dark inside.
  ink.rect(-170, 20, 340, 242, { fill: p.ink, fillAlpha: 0.82, width: 3, offset: 0 });
  // The stuff packed to the rafters, seen in the dark.
  if (open > 0.15) {
    const stack = [
      [-150, 160, 70, 100],
      [-75, 120, 60, 140],
      [-10, 175, 80, 85],
      [75, 140, 80, 120],
      [-120, 70, 90, 90],
      [10, 80, 110, 95],
    ] as const;
    for (const [x, y, w, h] of stack) {
      ink.rect(x, y, w, h, { fill: p.wood, fillAlpha: 0.6, width: 2, alpha: 0.6, offset: 0 });
    }
  }
  // The door, rolled up by `open`.
  const bottom = mix(262, 22, open);
  if (bottom > 24) {
    const top = 20;
    ink.rect(-170, top, 340, bottom - top, { fill: p.paper, width: 3, offset: 0 });
    ink.wash(
      [
        { x: -170, y: top },
        { x: 170, y: top },
        { x: 170, y: bottom },
        { x: -170, y: bottom },
      ],
      p.siding,
      0.45,
      2,
    );
    for (let y = bottom - 60; y > top + 4; y -= 60) {
      ink.line(-170, y, 170, y, { width: 2, overshoot: false });
    }
    ink.rect(-24, bottom - 22, 48, 9, { fill: p.roof, width: 2, offset: 0 });
  }
}

export type Junk = "mower" | "box" | "bike" | "rake" | "ball" | "can" | "kayak" | "tire" | "lamp";

/** One of the things a full garage holds, drawn about its middle. */
export function junk(ink: Ink, kind: Junk, x: number, y: number, rotation: number, t: number): void {
  const p = ink.palette;
  const { ctx } = ink;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  switch (kind) {
    case "mower":
      mower(ink, 0, 0, t * 6);
      break;
    case "box":
      ink.rect(-34, -28, 68, 56, { fill: p.wood, width: 2.6 });
      ink.line(-34, -6, 34, -6, { width: 1.6, alpha: 0.6, overshoot: false });
      ink.line(-6, -28, -6, -6, { width: 6, color: p.woodDark, alpha: 0.7, overshoot: false });
      break;
    case "bike": {
      ink.circle(-30, 14, 20, { width: 2.6 });
      ink.circle(30, 14, 20, { width: 2.6 });
      ink.poly(
        [
          { x: -30, y: 14 },
          { x: -6, y: -14 },
          { x: 22, y: -14 },
          { x: 30, y: 14 },
          { x: 0, y: 14 },
          { x: -6, y: -14 },
        ],
        false,
        { width: 2.8, color: p.signal },
      );
      ink.line(22, -14, 26, -28, { width: 2.6, overshoot: false });
      ink.line(18, -28, 32, -28, { width: 3, overshoot: false });
      ink.line(-10, -20, 4, -20, { width: 4, overshoot: false });
      break;
    }
    case "rake":
      ink.line(-60, 0, 46, 0, { width: 3.6, color: p.woodDark });
      ink.line(46, -16, 46, 16, { width: 3.4, overshoot: false });
      for (let i = -14; i <= 14; i += 7) {
        ink.line(46, i, 56, i, { width: 2, overshoot: false });
      }
      break;
    case "ball":
      ink.circle(0, 0, 17, { fill: p.signal, width: 2.6 });
      ink.arc(0, 0, 17, -0.6, 0.9, { width: 2, color: p.trim });
      break;
    case "can":
      ink.rect(-16, -20, 32, 40, { fill: p.siding, width: 2.6 });
      ink.ellipse(0, -20, 16, 5, { fill: p.trim, width: 2, offset: 0 });
      ink.curve(
        [
          { x: -15, y: -20 },
          { x: 0, y: -40 },
          { x: 15, y: -20 },
        ],
        { width: 1.8 },
      );
      ink.wash(
        [
          { x: -16, y: -12 },
          { x: 16, y: -12 },
          { x: 16, y: 4 },
          { x: -16, y: 4 },
        ],
        p.accent,
        0.9,
        0,
      );
      break;
    case "kayak":
      ink.ellipse(0, 0, 92, 14, { fill: p.accent, width: 2.8 });
      ink.ellipse(0, -2, 22, 6, { fill: p.ink, fillAlpha: 0.8, width: 2, offset: 0 });
      break;
    case "tire":
      ink.circle(0, 0, 24, { fill: p.ink, fillAlpha: 0.85, width: 2.6, offset: 0 });
      ink.circle(0, 0, 10, { fill: p.paper, width: 2, offset: 0 });
      break;
    case "lamp":
      ink.line(0, 24, 0, -14, { width: 2.8, overshoot: false });
      ink.line(-14, 24, 14, 24, { width: 3, overshoot: false });
      ink.shape(
        [
          { x: -16, y: -14 },
          { x: 16, y: -14 },
          { x: 10, y: -36 },
          { x: -10, y: -36 },
        ],
        { fill: p.accent, width: 2.4 },
      );
      break;
  }
  ctx.restore();
}

/** A push mower, standing on its wheels at `y`, its handle back to the left. */
export function mower(ink: Ink, x: number, y: number, spin: number): Pt {
  const p = ink.palette;
  ink.shape(
    [
      { x: x - 34, y: y + 10 },
      { x: x + 34, y: y + 10 },
      { x: x + 28, y: y - 12 },
      { x: x - 26, y: y - 14 },
    ],
    { fill: p.signal, width: 2.8 },
  );
  ink.rect(x - 12, y - 26, 26, 14, { fill: p.roof, width: 2.4, offset: 0 });
  for (const wx of [x - 24, x + 24]) {
    ink.circle(wx, y + 14, 10, { fill: p.ink, fillAlpha: 0.9, width: 2.2, offset: 0 });
    ink.line(wx, y + 14, wx + Math.cos(spin) * 7, y + 14 + Math.sin(spin) * 7, {
      width: 1.6,
      color: p.trim,
      overshoot: false,
    });
  }
  const grip = { x: x - 74, y: y - 66 };
  ink.line(x - 26, y - 6, grip.x, grip.y, { width: 3.4, overshoot: false });
  ink.line(grip.x - 2, grip.y - 8, grip.x + 2, grip.y + 8, { width: 4, overshoot: false });
  return grip;
}

// ---- thinking, designing, buying -------------------------------------------------------

/** A thought bubble: a trail of circles up from `from`, and a cloud at `at`. */
export function thought(ink: Ink, from: Pt, at: Pt, k: number, radius: number): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  for (let i = 0; i < 3; i += 1) {
    const ki = span(k, i * 0.12, i * 0.12 + 0.2);
    if (ki <= 0) {
      continue;
    }
    const m = (i + 1) / 4;
    ink.circle(mix(from.x, at.x - radius * 0.6, m), mix(from.y, at.y + radius * 0.7, m), (4 + i * 3) * ease.back(ki), {
      fill: p.paper,
      width: 2.4,
      offset: 0,
    });
  }
  const kc = ease.back(span(k, 0.36, 0.62));
  if (kc <= 0) {
    return;
  }
  const r = radius * kc;
  const lobes = 9;
  const points: Pt[] = [];
  for (let i = 0; i <= lobes * 6; i += 1) {
    const a = (i / (lobes * 6)) * Math.PI * 2;
    const bump = Math.abs(Math.sin(a * lobes * 0.5)) * 0.12;
    points.push({ x: at.x + Math.cos(a) * r * (1.25 + bump), y: at.y + Math.sin(a) * r * (0.85 + bump) });
  }
  ink.wash(points, p.paper, 1, 0);
  ink.curve(points, { width: 3 });
}

/** The pointer: an arrow with its tip at (x, y), and a ring when it clicks. */
export function cursor(ink: Ink, x: number, y: number, click: number): void {
  const p = ink.palette;
  if (click > 0 && click < 1) {
    ink.circle(x, y, 2 + click * 7, { width: 1, color: p.accent, alpha: 1 - click });
  }
  ink.shape(
    [
      { x, y },
      { x: x + 0.2, y: y + 9 },
      { x: x + 2.4, y: y + 7 },
      { x: x + 4.2, y: y + 10.6 },
      { x: x + 5.6, y: y + 9.8 },
      { x: x + 3.8, y: y + 6.3 },
      { x: x + 6.6, y: y + 6 },
    ],
    { fill: p.trim, width: 0.9, overshoot: false, offset: 0 },
  );
}

export function printer(ink: Ink, x: number, y: number, busy: number): void {
  const p = ink.palette;
  const shake = busy > 0 ? Math.sin(busy * 80) * 0.8 : 0;
  ink.rect(x - 34 + shake, y - 26, 68, 30, { fill: p.trim, width: 2.4 });
  ink.rect(x - 26 + shake, y - 32, 52, 7, { fill: p.roof, width: 2, offset: 0 });
  ink.line(x - 22 + shake, y - 12, x + 22 + shake, y - 12, { width: 2.6, overshoot: false });
  ink.dot(x + 24 + shake, y - 20, 2.2, busy > 0 && Math.sin(busy * 30) > 0 ? p.grass : p.shadow);
}

/** A sheet of the plan, with a drawing on it and its name across the top. */
export function planSheet(ink: Ink, x: number, y: number, rotation: number, title: string, scale = 1): void {
  const p = ink.palette;
  const { ctx } = ink;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.scale(scale, scale);
  ink.rect(-22, -28, 44, 56, { fill: p.trim, width: 1.8, offset: 1.2 });
  ink.text(title, 0, -21, { size: 5.2, weight: 800 });
  if (title === "CUT LIST") {
    for (let i = 0; i < 6; i += 1) {
      ink.line(-16, -12 + i * 7, 16 - (i % 3) * 6, -12 + i * 7, { width: 1, alpha: 0.7, overshoot: false });
    }
  } else if (title === "GUIDE") {
    for (let i = 0; i < 4; i += 1) {
      ink.dot(-15, -10 + i * 9, 1.4, p.accent);
      ink.line(-11, -10 + i * 9, 15, -10 + i * 9, { width: 1, alpha: 0.7, overshoot: false });
    }
  } else {
    ink.poly(
      [
        { x: -14, y: 18 },
        { x: -14, y: -2 },
        { x: 0, y: -12 },
        { x: 14, y: -2 },
        { x: 14, y: 18 },
      ],
      false,
      { width: 1.2 },
    );
    for (let sx = -10; sx <= 10; sx += 5) {
      ink.line(sx, 18, sx, 0, { width: 0.8, alpha: 0.7, overshoot: false });
    }
  }
  ctx.restore();
}

// ---- the lumber yard and the road -------------------------------------------------------

export function racks(ink: Ink, view: View, floor: number): void {
  const p = ink.palette;
  const shelves = [floor - 230, floor - 140, floor - 50];
  for (let x = Math.floor(view.left / 260) * 260; x < view.right + 260; x += 260) {
    ink.line(x, floor, x, floor - 290, { width: 4, color: p.roof });
    for (const [i, sy] of shelves.entries()) {
      ink.line(x, sy, x + 260, sy, { width: 3.4, color: p.roof, overshoot: false });
      const kind = Math.abs(noise(x, i)) ;
      const fill = kind < 0.33 ? p.wood : kind < 0.66 ? p.siding : p.woodDark;
      for (let b = 0; b < 4; b += 1) {
        ink.rect(x + 12, sy - 9 * (b + 1), 236, 8, { fill, width: 1.4, offset: 0.6, overshoot: false });
      }
    }
  }
}

/** A hanging sign with a word on it. */
export function sign(ink: Ink, x: number, y: number, word: string, swing: number): void {
  const p = ink.palette;
  const { ctx } = ink;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(swing);
  ink.line(-70, -40, -60, 0, { width: 1.8, overshoot: false });
  ink.line(70, -40, 60, 0, { width: 1.8, overshoot: false });
  ink.rect(-110, 0, 220, 56, { fill: p.woodDark, width: 3 });
  ink.text(word, 0, 30, { size: 34, weight: 900, color: p.trim, tracking: 0.12 });
  ctx.restore();
}

/** A flat cart, its handle's grip at the returned point. */
export function cart(ink: Ink, x: number, floor: number, spin: number): Pt {
  const p = ink.palette;
  ink.rect(x, floor - 32, 190, 12, { fill: p.roof, width: 2.6 });
  for (const wx of [x + 22, x + 168]) {
    ink.circle(wx, floor - 12, 12, { fill: p.ink, fillAlpha: 0.9, width: 2.2, offset: 0 });
    ink.line(wx, floor - 12, wx + Math.cos(spin) * 8, floor - 12 + Math.sin(spin) * 8, {
      width: 1.8,
      color: p.trim,
      overshoot: false,
    });
  }
  ink.line(x + 4, floor - 26, x - 8, floor - 128, { width: 4, color: p.roof });
  return { x: x - 8, y: floor - 124 };
}

export type Load = "concrete" | "studs" | "treated" | "joists" | "plywood" | "siding" | "shingles";

/** One item of the bill on its way to the cart: a bundle drawn about its bottom middle. */
export function load(ink: Ink, kind: Load, x: number, y: number, rotation: number): void {
  const p = ink.palette;
  const { ctx } = ink;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  switch (kind) {
    case "studs":
    case "treated":
    case "joists": {
      const h = kind === "joists" ? 10 : 7;
      // Treated lumber has the green of its preservative.
      const fill = kind === "treated" ? p.grass : p.wood;
      for (let i = 0; i < 3; i += 1) {
        ink.rect(-100, -h * (i + 1), 200, h, { fill, width: 1.6, offset: 0.6, overshoot: false });
      }
      ink.line(-40, -h * 3 - 1, -40, 0, { width: 2, color: p.signal, overshoot: false });
      ink.line(40, -h * 3 - 1, 40, 0, { width: 2, color: p.signal, overshoot: false });
      break;
    }
    case "plywood":
      ink.rect(-90, -14, 180, 14, { fill: p.wood, width: 2 });
      ink.line(-90, -7, 90, -7, { width: 1, alpha: 0.5, overshoot: false });
      break;
    case "siding":
      ink.rect(-90, -12, 180, 12, { fill: p.siding, width: 2 });
      break;
    case "concrete":
      // Two sacks of mix, banded.
      for (const dx of [-62, 8]) {
        ink.shape(
          [
            { x: dx, y: 0 },
            { x: dx + 54, y: 0 },
            { x: dx + 50, y: -20 },
            { x: dx + 4, y: -22 },
          ],
          { fill: p.shadow, width: 2 },
        );
        ink.line(dx + 6, -12, dx + 48, -11, { width: 4, color: p.roof, overshoot: false });
      }
      break;
    case "shingles":
      for (const dx of [-50, 6]) {
        ink.rect(dx, -18, 44, 18, { fill: p.roof, width: 2 });
      }
      break;
  }
  ctx.restore();
}

/** A clipboard with a list on it, each line ticked as it is loaded. */
export function checklist(ink: Ink, x: number, y: number, title: string, lines: readonly string[], ticks: readonly number[]): void {
  const p = ink.palette;
  ink.rect(x, y, 230, 54 + lines.length * 36, { fill: p.woodDark, width: 3 });
  ink.rect(x + 12, y + 16, 206, 30 + lines.length * 36, { fill: p.trim, width: 2, offset: 0 });
  ink.rect(x + 80, y - 8, 70, 22, { fill: p.roof, width: 2.4, offset: 0 });
  ink.text(title, x + 115, y + 34, { size: 19, weight: 800 });
  lines.forEach((line, i) => {
    const ly = y + 70 + i * 36;
    ink.rect(x + 28, ly - 11, 22, 22, { fill: p.paper, width: 2, offset: 0, overshoot: false });
    ink.text(line, x + 62, ly, { size: 20, weight: 600, align: "left" });
    const k = ticks[i] ?? 0;
    if (k > 0) {
      ink.poly(
        [
          { x: x + 30, y: ly - 1 },
          { x: x + 39, y: ly + 9 },
          { x: x + 56, y: ly - 18 },
        ],
        false,
        { width: 5, color: p.accent, draw: ease.out(k) },
      );
    }
  });
}

/** The builder's car, a load of lumber on the roof. Returns where the driver's head is. */
export function car(ink: Ink, x: number, ground: number, t: number, speed: number): Pt {
  const p = ink.palette;
  const bump = Math.sin(t * 40) * 1.4 * speed;
  const y = ground - 26 + bump;
  // The load overhangs at the back, a flag on the end of it.
  ink.rect(x - 150, y - 112, 250, 12, { fill: p.wood, width: 2.2 });
  ink.rect(x - 140, y - 122, 220, 10, { fill: p.wood, width: 2 });
  const flap = Math.sin(t * 22) * 6 * (0.4 + speed);
  ink.shape(
    [
      { x: x - 150, y: y - 112 },
      { x: x - 176, y: y - 108 + flap },
      { x: x - 150, y: y - 98 },
    ],
    { fill: p.signal, width: 1.8, offset: 0 },
  );
  // The body.
  ink.shape(
    [
      { x: x - 120, y: y + 6 },
      { x: x + 130, y: y + 6 },
      { x: x + 134, y: y - 40 },
      { x: x + 70, y: y - 48 },
      { x: x + 34, y: y - 96 },
      { x: x - 100, y: y - 96 },
      { x: x - 122, y: y - 46 },
    ],
    { fill: p.trim, width: 3 },
  );
  ink.wash(
    [
      { x: x - 120, y: y - 18 },
      { x: x + 132, y: y - 18 },
      { x: x + 132, y: y - 8 },
      { x: x - 120, y: y - 8 },
    ],
    p.siding,
    1,
    0,
  );
  ink.shape(
    [
      { x: x - 10, y: y - 52 },
      { x: x + 60, y: y - 52 },
      { x: x + 30, y: y - 88 },
      { x: x - 10, y: y - 88 },
    ],
    { fill: p.glass, width: 2.2, offset: 0 },
  );
  ink.shape(
    [
      { x: x - 100, y: y - 52 },
      { x: x - 22, y: y - 52 },
      { x: x - 22, y: y - 88 },
      { x: x - 90, y: y - 88 },
    ],
    { fill: p.glass, width: 2.2, offset: 0 },
  );
  for (const wx of [x - 76, x + 84]) {
    ink.circle(wx, ground - 18, 22, { fill: p.ink, fillAlpha: 0.9, width: 2.4, offset: 0 });
    ink.circle(wx, ground - 18, 9, { fill: p.roof, width: 1.6, offset: 0 });
    const spin = t * 30 * speed;
    ink.line(wx - Math.cos(spin) * 9, ground - 18 - Math.sin(spin) * 9, wx + Math.cos(spin) * 9, ground - 18 + Math.sin(spin) * 9, {
      width: 1.6,
      color: p.trim,
      overshoot: false,
    });
  }
  ink.dot(x + 128, y - 30, 4, p.accent);
  return { x: x + 18, y: y - 66 };
}

// ---- the yard ----------------------------------------------------------------------------

/**
 * A picket fence across the view. Each picket is solid — paper under a wash of wood — so
 * what is behind the fence (a low sun) shows only in the gaps between the boards.
 */
export function fence(ink: Ink, view: View, top: number, bottom: number): void {
  const p = ink.palette;
  const start = Math.floor(view.left / 34) * 34;
  for (let x = start; x < view.right + 34; x += 34) {
    const picket = [
      { x: x + 3, y: bottom },
      { x: x + 3, y: top + 8 },
      { x: x + 15, y: top },
      { x: x + 27, y: top + 8 },
      { x: x + 27, y: bottom },
    ];
    ink.wash(picket, p.paper, 1, 0);
    ink.wash(picket, p.wood, 0.45, 0);
    ink.poly(picket, false, { width: 1.6, alpha: 0.75 });
  }
  ink.line(view.left - 20, top + 26, view.right + 20, top + 26, { width: 2.2, alpha: 0.75 });
  ink.line(view.left - 20, bottom - 22, view.right + 20, bottom - 22, { width: 2.2, alpha: 0.75 });
}

export function tree(ink: Ink, x: number, ground: number, sway: number): void {
  const p = ink.palette;
  ink.shape(
    [
      { x: x - 14, y: ground },
      { x: x + 14, y: ground },
      { x: x + 9, y: ground - 150 },
      { x: x - 9, y: ground - 150 },
    ],
    { fill: p.woodDark, width: 2.6 },
  );
  const crown: Pt[] = [];
  const cx = x + sway * 6;
  const cy = ground - 220;
  for (let i = 0; i <= 60; i += 1) {
    const a = (i / 60) * Math.PI * 2;
    const bump = 1 + Math.abs(Math.sin(a * 3.5)) * 0.14;
    crown.push({ x: cx + Math.cos(a) * 110 * bump, y: cy + Math.sin(a) * 92 * bump });
  }
  ink.wash(crown, p.grass, 1, 4);
  ink.curve(crown, { width: 2.8 });
  ink.curve(
    [
      { x: cx - 40, y: cy + 10 },
      { x: cx - 10, y: cy + 30 },
      { x: cx + 20, y: cy + 18 },
    ],
    { width: 1.8, alpha: 0.6 },
  );
}

export function sun(ink: Ink, x: number, y: number, t: number): void {
  const p = ink.palette;
  for (let i = 0; i < 10; i += 1) {
    const a = (i / 10) * Math.PI * 2 + t * 0.6;
    ink.line(x + Math.cos(a) * 44, y + Math.sin(a) * 44, x + Math.cos(a) * 62, y + Math.sin(a) * 62, {
      width: 3,
      color: p.accent,
      overshoot: false,
    });
  }
  ink.circle(x, y, 34, { fill: p.accent, width: 2.6 });
}

export function cloud(ink: Ink, x: number, y: number, size: number): void {
  const p = ink.palette;
  const points: Pt[] = [];
  for (let i = 0; i <= 48; i += 1) {
    const a = (i / 48) * Math.PI * 2;
    const bump = a > Math.PI ? Math.abs(Math.sin(a * 3)) * 0.35 : 0;
    points.push({ x: x + Math.cos(a) * size * 1.6, y: y + Math.sin(a) * size * (0.45 + bump) });
  }
  ink.wash(points, p.trim, 0.9, 0);
  ink.curve(points, { width: 2.2, alpha: 0.7 });
}

export function ladder(ink: Ink, foot: Pt, top: Pt): void {
  const p = ink.palette;
  for (const dx of [-14, 14]) {
    ink.line(foot.x + dx, foot.y, top.x + dx, top.y, { width: 3.4, color: p.woodDark });
  }
  for (let k = 0.1; k < 1; k += 0.14) {
    const x = mix(foot.x, top.x, k);
    const y = mix(foot.y, top.y, k);
    ink.line(x - 14, y, x + 14, y, { width: 2.4, overshoot: false });
  }
}

export function bird(ink: Ink, x: number, y: number, flap: number, perched: boolean): void {
  const p = ink.palette;
  ink.ellipse(x, y, 12, 8, { fill: p.siding, width: 2.2, offset: 0 });
  ink.circle(x + 10, y - 7, 6, { fill: p.siding, width: 2, offset: 0 });
  ink.dot(x + 12, y - 8, 1.2);
  ink.shape(
    [
      { x: x + 15, y: y - 8 },
      { x: x + 22, y: y - 6 },
      { x: x + 15, y: y - 4 },
    ],
    { fill: p.accent, width: 1.2, offset: 0 },
  );
  if (perched) {
    ink.line(x - 2, y + 7, x - 3, y + 13, { width: 1.6, overshoot: false });
    ink.line(x + 4, y + 7, x + 5, y + 13, { width: 1.6, overshoot: false });
    ink.curve(
      [
        { x: x - 6, y: y - 2 },
        { x: x - 2, y: y + 2 },
        { x: x + 6, y: y - 1 },
      ],
      { width: 1.8 },
    );
  } else {
    const wing = Math.sin(flap) * 14;
    ink.poly(
      [
        { x: x - 6, y: y - 2 },
        { x: x - 2, y: y - 6 - wing },
        { x: x + 6, y: y - 2 },
      ],
      false,
      { width: 2.2 },
    );
  }
}

/** A musical note, for a bird that has something to say. */
export function note(ink: Ink, x: number, y: number, k: number): void {
  if (k <= 0 || k >= 1) {
    return;
  }
  const alpha = Math.sin(k * Math.PI);
  ink.ellipse(x, y - k * 30, 4.5, 3.4, { fill: ink.palette.ink, alpha, width: 1.2, offset: 0 }, -0.4);
  ink.line(x + 4, y - k * 30, x + 4, y - 18 - k * 30, { width: 1.8, alpha, overshoot: false });
}

/** A page of the build guide, held up in the corner: the step the film is on. */
export function guideCard(ink: Ink, x: number, y: number, title: string, flip: number): void {
  const p = ink.palette;
  const { ctx } = ink;
  const width = 300;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-0.03);
  const squash = flip < 1 ? Math.abs(Math.cos(flip * Math.PI)) : 1;
  ctx.scale(1, Math.max(0.05, squash));
  const lines = wrap(ink, title, width - 36, { size: 24, weight: 800 });
  const height = 56 + lines.length * 30;
  ink.rect(0, 0, width, height, { fill: p.trim, width: 2.6, offset: 3 });
  ink.text("BUILD GUIDE", 18, 22, { size: 14, weight: 800, align: "left", color: p.accent, tracking: 0.14 });
  lines.forEach((line, i) => {
    ink.text(line, 18, 52 + i * 30, { size: 24, weight: 800, align: "left" });
  });
  ctx.restore();
}

function wrap(ink: Ink, text: string, width: number, style: { size: number; weight: number }): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line === "" ? word : `${line} ${word}`;
    if (ink.measure(next, style) > width && line !== "") {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line !== "") {
    lines.push(line);
  }
  return lines;
}

/**
 * The end card, drawn as the title block in the corner of a plan sheet: whose drawing it
 * is, what it is, and what it costs.
 */
export function titleBlock(
  ink: Ink,
  k: number,
  site: { brand: string; price: string; host: string },
  words: { kind: string; line1: string; line2: string },
): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  const { ctx } = ink;
  const rise = (1 - ease.back(k)) * 420;
  const left = -390;
  const right = 390;
  const top = 150;
  const bottom = 420;
  const strip = 362;
  const split = 160;
  ctx.save();
  ctx.translate(0, rise);
  const outer: Paint = { fill: p.trim, width: 4.4, offset: 6 };
  ink.rect(left, top, right - left, bottom - top, outer);
  ink.rect(left + 9, top + 9, right - left - 18, bottom - top - 18, { width: 1.6, overshoot: true });
  ink.line(left + 9, strip, right - 9, strip, { width: 1.8 });
  ink.line(split, top + 9, split, strip, { width: 1.8 });
  ink.text(site.brand.toUpperCase(), left + 34, top + 44, { size: 22, weight: 900, align: "left", tracking: 0.16 });
  ink.text(words.line1, left + 32, top + 104, { size: 46, weight: 900, align: "left" });
  ink.text(words.line2, left + 32, top + 160, { size: 46, weight: 900, align: "left" });
  ink.text(site.price, (split + right) / 2, top + 98, { size: 104, weight: 900, color: p.accent });
  ink.text(words.kind, (split + right) / 2, top + 168, { size: 18, weight: 800, tracking: 0.18 });
  const cells = [site.host === "" ? "Design yours online" : site.host, "SCALE: YOURS", "DESIGNED BY: YOU"];
  const cellW = (right - left - 18) / cells.length;
  cells.forEach((cell, i) => {
    const cx = left + 9 + cellW * (i + 0.5);
    if (i > 0) {
      ink.line(left + 9 + cellW * i, strip, left + 9 + cellW * i, bottom - 9, { width: 1.6 });
    }
    ink.text(cell, cx, (strip + bottom - 9) / 2 + 1, { size: i === 0 ? 20 : 17, weight: 800, tracking: i === 0 ? 0.02 : 0.12 });
  });
  ctx.restore();
}
